import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Modal, Row, Col, Select, TreeSelect, Tooltip } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { getUserHierarchyFunc } from "../../apis/dashboard/hierarchy";
import { APP_LANDING_PATH, APP_PREFIX_PATH } from "../../configs/AppConfig";

const { Option } = Select;

/** Hide nav team picker on pages that have their own company/team context UI (avoid duplicate controls). */
const HIDE_SELECTOR_PREFIXES = [
  `${APP_PREFIX_PATH}/company-management`,
  `${APP_PREFIX_PATH}/user-management`,
  `${APP_PREFIX_PATH}/cxo-management`,
  `${APP_PREFIX_PATH}/contract-management`,
  `${APP_PREFIX_PATH}/obligation-management`,
  `${APP_PREFIX_PATH}/obligation-mapping`,
  `${APP_PREFIX_PATH}/team-obligation-user`,
];

/** Routes where the selector is shown but only the Company picker is enabled — the Team picker is hidden because the page is company-scoped (analytics reports). */
const COMPANY_ONLY_PREFIXES = [
  `${APP_PREFIX_PATH}/analytics/reports`,
];

const shouldShowTeamSelector = (pathname) =>
  !HIDE_SELECTOR_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const isCompanyOnlyRoute = (pathname) =>
  COMPANY_ONLY_PREFIXES.some((prefix) => pathname.startsWith(prefix));

function transformListToTree(data) {
  const nodeMap = new Map();
  const rootNodes = [];
  data.forEach((item) => {
    nodeMap.set(item.id, {
      value: item.uuid,
      title: item.title,
      id: item.id,
      disabled: item.disabled,
      division: item.header ?? null,
      children: [],
    });
  });
  data.forEach((item) => {
    if (item.parent) {
      const parentNode = nodeMap.get(item.parent);
      if (parentNode) parentNode.children.push(nodeMap.get(item.id));
    } else {
      rootNodes.push(nodeMap.get(item.id));
    }
  });
  return rootNodes.length === 1 ? rootNodes[0].children : rootNodes;
}

function getOrderedUnifiedHierarchy(data, targetUuids) {
  const nodeMap = data.reduce((map, node) => {
    map[node.id] = node;
    return map;
  }, {});
  const includedNodes = new Set();
  const markHierarchyForUuid = (uuid) => {
    const targetNode = data.find((node) => node.uuid === uuid);
    if (!targetNode) return;
    let currentNode = targetNode;
    while (currentNode) {
      includedNodes.add(currentNode.id);
      currentNode = currentNode.parent ? nodeMap[currentNode.parent] : null;
    }
  };
  targetUuids.forEach(markHierarchyForUuid);
  return data.filter((node) => includedNodes.has(node.id));
}

export const NavTeamSelector = ({
  companyList,
  companyLoading,
  isAuthenticated,
}) => {
  const location = useLocation();
  const pathname = location?.pathname || "";
  const isMountedRef = useRef(true);

  const [visible, setVisible] = useState(false);
  const [listCompany, setListCompany] = useState({});
  const [listCompanyData, setListCompanyData] = useState([]);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [value, setValue] = useState(undefined);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setListCompanyData([]);
      setListCompany({});
      return;
    }
    if (!companyLoading && companyList?.length > 0) {
      // Align with KCRA: include all companies with status, no role filter
      const result = companyList
        .filter((e) => e.status)
        .map((e) => ({ key: e.id, value: e.id, label: e.name }));
      setListCompanyData(result);
      const stored = localStorage.getItem("company_selected");
      if (stored && result.some((r) => parseInt(r.value) === parseInt(stored))) {
        const found = result.find((r) => parseInt(r.value) === parseInt(stored));
        setListCompany({ key: found.value, label: found.label, value: found.value });
      } else if (result.length > 0) {
        const first = result[0];
        localStorage.setItem("company_selected", first.value);
        localStorage.setItem("company_selected_name", first.label);
        setListCompany({ key: first.value, label: first.label, value: first.value });
      }
    } else {
      setListCompanyData([]);
    }
  }, [isAuthenticated, companyList, companyLoading]);

  useEffect(() => {
    if (!isAuthenticated || !listCompany?.key) {
      setHierarchyData([]);
      setTreeData([]);
      setValue(undefined);
      return;
    }
    getUserHierarchyFunc({
      company: parseInt(listCompany.key, 10),
      is_shorter: true,
    })
      .then((res) => {
        if (!isMountedRef.current) return;
        if (res?.data?.statuscode === 200 && res?.data?.entity_meta) {
          const meta = res.data.entity_meta.map((ele) => ({
            ...ele,
            disabled: !ele.isActive,
            division: ele?.div_data?.id ?? null,
            value: ele.uuid,
            label: ele.title,
          }));
          setHierarchyData(meta);
        } else {
          setHierarchyData([]);
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setHierarchyData([]);
      });
  }, [isAuthenticated, listCompany?.key]);

  useEffect(() => {
    if (hierarchyData.length > 0) {
      const activeUuids = hierarchyData.filter((el) => !el.disabled).map((el) => el.uuid);
      if (activeUuids.length > 0) {
        const ordered = getOrderedUnifiedHierarchy(hierarchyData, activeUuids);
        const tree = transformListToTree(ordered);
        setTreeData(tree ?? []);
        const storedTeamId = localStorage.getItem("team_selected_id");
        const storedMatch =
          storedTeamId &&
          ordered.find((el) => el.division != null && parseInt(String(el.division), 10) === parseInt(storedTeamId, 10) && !el.disabled);
        const target = storedMatch || ordered.filter((el) => !el.disabled)[0];
        if (target) {
          const isDefault = !storedMatch;
          if (isDefault && target.division != null) {
            localStorage.setItem("team_selected_id", String(target.division));
            localStorage.setItem("team_selected_name", target.title || "");
          }
          const label = storedMatch ? (localStorage.getItem("team_selected_name") || target.title) : target.title;
          setValue({ value: target.uuid, label });
        }
      } else {
        setTreeData([]);
        setValue(undefined);
      }
    } else {
      setTreeData([]);
      setValue(undefined);
    }
  }, [hierarchyData]);

  const displayLabel =
    localStorage.getItem("team_selected_name") || localStorage.getItem("company_selected_name") || "Select Team";

  const handleSave = () => {
    if (listCompany?.value) {
      localStorage.setItem("company_selected", listCompany.value);
      localStorage.setItem("company_selected_name", listCompany.label);
    }
    if (value?.value) {
      const selectedItem = hierarchyData.find((el) => el.uuid === value.value);
      const teamId = selectedItem?.division ?? selectedItem?.div_data?.id ?? null;
      if (teamId != null) {
        localStorage.setItem("team_selected_id", String(teamId));
      } else {
        localStorage.removeItem("team_selected_id");
      }
      localStorage.setItem("team_selected_name", value.label || "");
    } else {
      localStorage.removeItem("team_selected_id");
      localStorage.removeItem("team_selected_name");
    }
    setVisible(false);
    // Navigate to default (Tasks) and refresh the whole tool so all data uses new company/team
    window.location.href = `${window.location.origin}${APP_LANDING_PATH}`;
  };

  const handleCancel = () => {
    const stored = localStorage.getItem("company_selected");
    if (stored && listCompanyData.length > 0) {
      const found = listCompanyData.find((r) => parseInt(r.value) === parseInt(stored));
      if (found) setListCompany({ key: found.value, label: found.label, value: found.value });
    }
    setVisible(false);
  };

  if (!shouldShowTeamSelector(pathname)) {
    return null;
  }

  /** On analytics dashboards/reports the page is company-scoped; the team picker would mislead users and the in-page team filter handles per-team narrowing. */
  const companyOnly = isCompanyOnlyRoute(pathname);

  const canOpen = companyOnly
    ? listCompanyData.length > 1
    : listCompanyData.length > 1 ||
      hierarchyData.filter((el) => !el.disabled).length > 1;

  const tooltipTitle = companyOnly ? "Select Company" : "Select Team";
  const modalTitle = companyOnly ? "Select Company" : "Select Team";
  const triggerLabel = companyOnly
    ? localStorage.getItem("company_selected_name") || "Select Company"
    : displayLabel;

  return (
    <>
      <li className="ant-menu-item ant-menu-item-only-child">
        <Tooltip title={tooltipTitle} placement="right">
          <span
            onClick={() => canOpen && setVisible(true)}
            style={{
              fontSize: "13px",
              cursor: canOpen ? "pointer" : "default",
              opacity: canOpen ? 1 : 0.7,
              display: "inline-flex",
              alignItems: "center",
              maxWidth: "100%",
              minWidth: 0,
            }}
          >
            {/* Truncate an over-long team/company name so it never overflows the header. */}
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {triggerLabel}
            </span>
            <CaretDownOutlined style={{ fontSize: "13px", marginLeft: 4, flexShrink: 0 }} />
          </span>
        </Tooltip>
      </li>
      <Modal
        title={modalTitle}
        visible={visible}
        okText="Save"
        onOk={handleSave}
        onCancel={handleCancel}
      >
        <Row gutter={16}>
          <Col span={24} className="mb-2">
            <label className="mb-0 font-weight-semibold d-block" style={{ color: "rgb(26, 51, 83)" }}>
              Company
            </label>
            <Select
              showSearch
              labelInValue
              style={{ width: "100%" }}
              className="mt-1"
              placeholder="Select Company"
              optionFilterProp="children"
              value={listCompany?.value ? listCompany : null}
              onChange={(val) => {
                setListCompany(val);
                setValue(undefined);
              }}
              filterOption={(input, option) => {
                const label = option?.children ?? option?.label ?? "";
                return String(label).toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {listCompanyData.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Col>
          {!companyOnly && (
            <Col span={24}>
              <label className="mb-1 font-weight-semibold d-block" style={{ color: "rgb(26, 51, 83)" }}>
                Select Team
              </label>
              {treeData.length > 0 ? (
                <TreeSelect
                  style={{ width: "100%" }}
                  treeData={treeData}
                  value={value?.value}
                  onChange={(val, label) => setValue(val ? { value: val, label: label?.[0] || val } : undefined)}
                  treeDefaultExpandAll
                  placeholder="Select Team"
                  allowClear
                />
              ) : (
                <Select style={{ width: "100%" }} disabled placeholder="Select company first" />
              )}
            </Col>
          )}
        </Row>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  companyList: state.company?.data ?? [],
  companyLoading: state.company?.loading,
  isAuthenticated: state.auth?.isAuthenticated ?? false,
});

export default connect(mapStateToProps)(NavTeamSelector);
